import { useState, useEffect } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { capitalizeFirstLetter } from "@/utils/stringUtils.js";
import "react-datepicker/dist/react-datepicker.css";

import api from "@/api/api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const UserProfileForm = () => {
  const { t } = useTranslation();

  const minAgeDate = new Date();
  minAgeDate.setFullYear(minAgeDate.getFullYear() - 18);

  const [profileId, setProfileId] = useState(
    sessionStorage.getItem("profile") || "",
  );
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    lastname: "",
    birthDate: null,
    telephoneContact: "",
  });

  useEffect(() => {
    if (!profileId) return;
    api
      .get(`/user_profiles/${profileId}.json`)
      .then((response) => {
        setFormData({
          name: response.data.name || "",
          lastname: response.data.lastname || "",
          birthDate: response.data.birthDate
            ? new Date(response.data.birthDate)
            : null,
          telephoneContact: response.data.telephoneContact || "",
        });
      })
      .catch((error) => {
        console.error("ERROR:", error);
      });
  }, [profileId]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, birthDate: date });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      name: formData.name,
      lastname: formData.lastname,
      birthDate: formData.birthDate
        ? formData.birthDate.toISOString().slice(0, 10)
        : null,
      telephoneContact: formData.telephoneContact || null,
    };

    try {
      if (profileId) {
        await api.patch(`/user_profiles/${profileId}.json`, payload, {
          headers: { "Content-Type": "application/merge-patch+json" },
        });
      } else {
        const userId = sessionStorage.getItem("user");
        const { data } = await api.post(
          "/user_profiles",
          { ...payload, user: `/api/users/${userId}` },
          { headers: { "Content-Type": "application/ld+json" } },
        );
        sessionStorage.setItem("profile", data.id);
        setProfileId(data.id);
      }
      toast.success(capitalizeFirstLetter(t("saved successfully")));
    } catch (error) {
      console.error("ERROR:", error);
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.["hydra:description"] ||
          "No se pudo guardar el perfil",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <Form onSubmit={handleSubmit}>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-bold">
              {capitalizeFirstLetter(t("name"))}
            </Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name || ""}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-bold">
              {capitalizeFirstLetter(t("lastname"))}
            </Form.Label>
            <Form.Control
              type="text"
              name="lastname"
              value={formData.lastname || ""}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group className="w-100">
            <Form.Label className="fw-bold w-100">
              {capitalizeFirstLetter(t("date of birth"))}
            </Form.Label>
            <DatePicker
              selected={formData.birthDate}
              onChange={handleDateChange}
              style={{ width: "100%" }}
              className="form-control w-100"
              dateFormat="yyyy-MM-dd"
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              placeholderText={t("select_date")}
              maxDate={minAgeDate}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-bold">
              {capitalizeFirstLetter(t("telephone contact"))}
            </Form.Label>
            <Form.Control
              type="text"
              name="telephoneContact"
              value={formData.telephoneContact || ""}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>
      </Row>

      <Button type="submit" variant="primary" disabled={saving}>
        {capitalizeFirstLetter(t("save"))}
      </Button>
    </Form>
  );
};

export { UserProfileForm };
